import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { ArrowLeft, FileText, Scale, AlertOctagon, Cookie, RefreshCw } from "lucide-react";

/**
 * Terms & Conditions — DPDP Act 2023 aligned + Indian jurisdiction.
 *
 * Includes:
 *   - Medical liability disclaimer (zero liability for medical negligence)
 *   - Governing law: India, exclusive jurisdiction Jodhpur, Rajasthan
 *   - Right to modify without prior notice
 *   - Cookie policy reference
 *   - RGHS / Ayushman workflow disclosures
 */
export default function TermsAndConditions() {
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="terms-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <Card className="p-8 space-y-4">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-sky-600" />
            <h1 className="font-heading text-3xl font-semibold text-slate-900">Terms &amp; Conditions</h1>
          </div>
          <p className="text-xs text-slate-500">Effective from: Feb 2026 · Read together with the UpchaarQ Privacy Policy.</p>

          <section className="space-y-3 text-sm text-slate-700 leading-relaxed">
            {/* 1. USE OF PLATFORM */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-4">1. Use of the Platform</h2>
            <p>UpchaarQ (&quot;we&quot;, &quot;us&quot;, or the &quot;Platform&quot;) is an online queue-management, appointment-booking, and
              settlement platform for CMHO-empanelled hospitals, clinics, and their patients. By registering, you confirm you
              are legally authorised to represent the organisation and, where applicable, hold a valid CMHO / clinical
              establishment registration licence.</p>

            {/* 2. MEDICAL DISCLAIMER */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">
              <AlertOctagon className="w-4 h-4 inline mr-1 text-rose-600" />2. Medical Disclaimer &amp; Limitation of Liability
            </h2>
            <p>UpchaarQ is <span className="font-medium">solely a technology queue-management and booking tool</span>. UpchaarQ
              itself does <span className="font-medium">not</span> provide medical advice, diagnosis, second opinions, prescriptions,
              or treatment recommendations. All clinical judgements, diagnostic decisions, prescription content, dosage, and
              treatment outcomes remain the sole responsibility of the treating registered medical practitioner and the
              empanelled hospital / clinic.</p>
            <p><span className="font-medium">UpchaarQ shall bear zero liability for</span>: medical negligence, diagnosis accuracy,
              prescription errors, adverse drug reactions, doctor-patient disputes, delays in treatment, or any direct,
              indirect, incidental, consequential, or exemplary damages arising from a user&apos;s interaction with a
              medical practitioner discovered through the Platform. The Platform&apos;s role is limited strictly to
              facilitating queue tokens, appointment slots, and payment routing.</p>

            {/* 3. BOOKINGS & PAYMENTS */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">3. Bookings &amp; Payments</h2>
            <p>Consultation fees are set entirely by the hospital / clinic. UpchaarQ charges either ₹1 per booking during
              the first-month free trial or the % commission agreed at KYC approval. Payments are processed via Razorpay;
              refunds follow Razorpay&apos;s standard T-7-day cycle.</p>

            {/* 4. GOV SCHEMES */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">4. Government Cashless Schemes (RGHS &amp; Ayushman)</h2>
            <p>Appointments booked under the RGHS or Ayushman Bharat schemes bypass the patient-side gateway. The
              patient owes ₹0 at the time of booking; UpchaarQ&apos;s platform fee is deducted from the hospital&apos;s
              pre-funded wallet balance during the daily 8 PM settlement. RGHS / Ayushman card details entered by
              patients are audit-retained per the applicable government scheme rules and may be reviewed by empanelling
              authorities.</p>

            {/* 5. PROHIBITED */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">5. Prohibited Activities</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Creating appointments with false patient identities or fabricated CMHO licences.</li>
              <li>Attempting to bypass counter-verification for gov-scheme bookings.</li>
              <li>Extracting bulk data via automated scraping, screen-recording, or misuse of public endpoints.</li>
              <li>Attempts at reverse engineering, security probing, or exploitation of Platform vulnerabilities.</li>
            </ul>

            {/* 6. TERMINATION */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">6. Suspension &amp; Termination</h2>
            <p>UpchaarQ may suspend or terminate accounts that violate these Terms, with 24-hour notice for good-faith
              actors and immediate suspension for fraud, financial misconduct, or repeat gov-scheme rejection. Data export
              remains available for 30 days post-suspension in accordance with the Privacy Policy.</p>

            {/* 7. COOKIES */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">
              <Cookie className="w-4 h-4 inline mr-1 text-amber-600" />7. Cookie Usage
            </h2>
            <p>UpchaarQ uses only essential / functional cookies required for maintaining secure login sessions and
              generating queue tokens. We do not use third-party advertising trackers or cross-site profiling cookies.
              Full disclosure is provided in the <Link to="/legal/privacy" className="text-sky-600 hover:underline">Privacy Policy</Link>.</p>

            {/* 8. RIGHT TO MODIFY */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">
              <RefreshCw className="w-4 h-4 inline mr-1 text-slate-600" />8. Right to Modify
            </h2>
            <p>UpchaarQ reserves the right to modify, update, or replace these Terms &amp; Conditions <span className="font-medium">at any time
              without prior notice</span>. The most current version will always be published at this URL together with the effective
              date. <span className="font-medium">Continued use of the Platform after any such change constitutes your acceptance of the
              updated Terms.</span> Users are encouraged to review this page periodically.</p>

            {/* 9. GOVERNING LAW */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">
              <Scale className="w-4 h-4 inline mr-1 text-indigo-600" />9. Governing Law &amp; Jurisdiction
            </h2>
            <p>These Terms &amp; Conditions and the use of the Platform are <span className="font-medium">governed by the laws of
              India</span>, including the Information Technology Act 2000, the Digital Personal Data Protection Act 2023, the
              Clinical Establishments (Registration and Regulation) Act 2010, and other applicable central and state statutes.</p>
            <p>All disputes, controversies, or claims arising out of or in connection with these Terms shall be subject to the
              <span className="font-medium"> exclusive jurisdiction of the competent courts of Jodhpur, Rajasthan, India</span>.</p>

            {/* 10. CONTACT */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">10. Contact</h2>
            <p>For any question regarding these Terms or the Platform, write to
              <a className="text-sky-600 hover:underline mx-1" href="mailto:support@upchaarq.in">support@upchaarq.in</a>.</p>
          </section>
        </Card>
      </div>
    </div>
  );
}
